import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import Plate from '../components/Plate';
import { BASE_URL, sinistresAPI, assurancesAPI, genresAPI, marquesAPI, photosAPI, modelesAPI, carosseriesAPI, colorsAPI, energiesAPI, estadosAPI } from '../services/api';
import { Save, ArrowLeft, Car, User, Users, Shield, Camera, Upload, X, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import imageCompression from 'browser-image-compression';
import DictationButton from '../components/DictationButton';
import { playSound, useSoundPlayer } from '../utils/SoundPlayer';
import TemplateSuggester from '../components/TemplateSuggester';
import DatePicker, { registerLocale } from "react-datepicker";
import fr from 'date-fns/locale/fr';
import "react-datepicker/dist/react-datepicker.css";

registerLocale('fr', fr);

// Local Plate component removed in favor of shared component


const Field = ({ label, field, form, handleChange, type = 'text', required, placeholder, children, dictation }) => (
    <div className="form-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label className="form-label" style={{ margin: 0 }}>{label}{required && ' *'}</label>
            {dictation && (
                <DictationButton onTranscript={(text) => handleChange(field, (form[field] ? form[field] + ' ' : '') + text)} />
            )}
        </div>
        {children || (
            type === 'date' ? (
                <DatePicker
                    selected={form && field && form[field] ? new Date(form[field]) : null}
                    onChange={(date) => handleChange(field, date ? date.toISOString().split('T')[0] : '')}
                    className="form-control"
                    dateFormat="dd/MM/yyyy"
                    locale="fr"
                    placeholderText="jj/mm/aaaa"
                    required={required}
                />
            ) : (
                <input
                    type={type}
                    className="form-control"
                    placeholder={placeholder || label}
                    value={form && field ? (form[field] || '') : ''}
                    onChange={(e) => handleChange(field, e.target.value)}
                    required={required}
                    lang="fr-DZ"
                />
            )
        )}
        {dictation && form && field && (
            <TemplateSuggester
                field={field}
                value={form[field]}
                onSelect={(val) => handleChange(field, val)}
            />
        )}
    </div>
);

    const SinistreForm = () => {
    const { playSound } = useSoundPlayer();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({}); // Pour la validation visuelle
    const [assurances, setAssurances] = useState([]);
    const [genres, setGenres] = useState([]);
    const [marques, setMarques] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [modeles, setModeles] = useState([]);
    const [carosseries, setCarosseries] = useState([]);
    const [colors, setColors] = useState([]);
    const [energies, setEnergies] = useState([]);
    const [estados, setEstados] = useState([]);
    const [quickAdd, setQuickAdd] = useState(null); // { type: 'genre'|'carrosserie'|'color'|'energie'|'estado', value: '' }
    const processedAiData = useRef(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [modelYears, setModelYears] = useState([]);
    const location = useLocation();

    const [form, setForm] = useState({
        statut: 'brouillon',
        etat_vehicule: 'en_cours',
        date_sinistre: '',
        nom_assure: '',
        numero_sinistre: '', // N° compagnie assurance (manuel, optionnel)
        numero_police: '',
        marque_vehicule: '',
        modele_vehicule: '',
        immatriculation: '',
        annee_vehicule: '',
        type_vehicule: '',
        energie: '',
        puissance: '',
        carrosserie: '',
        couleur: '',
        numero_serie: '',
        genre: '',
        nom_tiers: '',
        nom_assurance: '',
        assurance_logo: '',
        code_assurance: '',
        ville_assurance: '',
        lieu_visite: '',
        reference_interne: '', // Auto-généré par le système (lecture seule)
    });

    const [clientSuggestions, setClientSuggestions] = useState([]);
    const [clientVehicles, setClientVehicles] = useState([]);
    const [showVehiclesModal, setShowVehiclesModal] = useState(false);
    const [searchingClients, setSearchingClients] = useState(false);

    useEffect(() => {
        // Load reference data
        Promise.all([assurancesAPI.list(), genresAPI.list(), marquesAPI.list(), carosseriesAPI.list(), colorsAPI.list(), energiesAPI.list(), estadosAPI.list()])
            .then(([a, g, m, c, cl, e, es]) => {
                setAssurances(a.data.data);
                setGenres(g.data.data);
                setMarques(m.data.data);
                setCarosseries(c.data.data);
                setColors(cl.data.data);
                setEnergies(e.data.data);
                setEstados(es.data.data);
            })
            .catch(() => { });

        if (isEditing) {
            setLoading(true);
            sinistresAPI.getById(id)
                .then(res => {
                    const s = res.data.data;
                    setForm(prev => ({
                        ...prev,
                        ...s,
                        annee_vehicule: s.annee_vehicule || '',
                        date_sinistre: (s.date_sinistre && typeof s.date_sinistre === 'string') ? s.date_sinistre.split('T')[0] : '',
                    }));
                    if (s.photos) {
                        setPhotos(s.photos.map(p => {
                            let photoUrl = p.file_path || p.url;
                            // If it's a local filesystem path or just missing, use filename fallback
                            if (!photoUrl || photoUrl.includes('\\') || (!photoUrl.startsWith('http') && !photoUrl.startsWith('/') && !photoUrl.startsWith('blob:'))) {
                                photoUrl = `${BASE_URL}/uploads/photos/${p.filename}`;
                            } else if (photoUrl.startsWith('/')) {
                                // Local path like /uploads/photos/xxx — prepend backend URL
                                photoUrl = `${BASE_URL}${photoUrl}`;
                            }
                            return { ...p, url: photoUrl };
                        }));
                    }
                })
                .catch(() => toast.error('Sinistre non trouvé'))
                .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEditing]);

    useEffect(() => {
        // If state is empty, reset the ref so the NEXT scan can be processed
        if (!location.state) {
            processedAiData.current = false;
            return;
        }

        // Only run if we have state and haven't processed THIS SPECIFIC state yet
        if (!isEditing && location.state && !processedAiData.current && assurances.length > 0) {
            processedAiData.current = true; // Mark as done
            console.log('[AI Scan] Processing AI data...');
            
            const aiData = { ...location.state };
            let insuranceMeta = {};

            // 1. Map Insurance (Priority: Code -> Name)
            if (aiData.code_assurance || aiData.nom_assurance) {
                // Try matching by Code first (User requirement)
                let match = aiData.code_assurance ? assurances.find(a => String(a.code) === String(aiData.code_assurance)) : null;

                // Fallback to Name match (with partial support)
                if (!match && aiData.nom_assurance) {
                    const searchName = aiData.nom_assurance.toUpperCase();
                    match = assurances.find(a => {
                        const dbName = a.name.toUpperCase();
                        return dbName === searchName || dbName.includes(searchName) || searchName.includes(dbName);
                    });
                }

                if (match) {
                    console.log('[AI Scan] Found matching insurance:', match.name);
                    insuranceMeta = {
                        nom_assurance: match.name,
                        assurance_logo: match.logo || '',
                        code_assurance: match.code || aiData.code_assurance || '',
                        ville_assurance: match.city || aiData.ville_assurance || '',
                    };
                } else {
                    console.warn('[AI Scan] No insurance match found. Clearing fields.');
                    aiData.nom_assurance = '';
                }
            }

            if (aiData.modele_vehicule && aiData.type_vehicule && aiData.modele_vehicule === aiData.type_vehicule) {
                aiData.modele_vehicule = '';
            }

            // 1.5 Format numero_sinistre (replace space with slash if it looks like YYYY XXXXXX)
            if (aiData.numero_sinistre && aiData.numero_sinistre.includes(' ')) {
                aiData.numero_sinistre = aiData.numero_sinistre.replace(/\s+/g, '/');
            }

            // 2. Normalization for Energie (Mapping to: G.O, ESS, ESS/GPL, HYBRID)
            if (aiData.energie) {
                const e = aiData.energie.toLowerCase();
                if (e.includes('gpl') || e.includes('gaz')) aiData.energie = 'ESS/GPL';
                else if (e.includes('hybrid') || e.includes('hibrid')) aiData.energie = 'HYBRID';
                else if (e.includes('ess') || e.includes('بنزين') || e.includes('رصاص') || e.includes('essence')) aiData.energie = 'ESS';
                else if (e.includes('die') || e.includes('maz') || e.includes('مازوت') || e.includes('g.o') || e.includes('diesel') || e.includes('go')) aiData.energie = 'G.O';
                else aiData.energie = ''; // Clear if unknown
            }

            setForm(prev => {
                const updatedFields = {};
                Object.keys(aiData).forEach(key => {
                    if (aiData[key] !== null && aiData[key] !== undefined && aiData[key] !== '') {
                        updatedFields[key] = aiData[key];
                    }
                });

                return {
                    ...prev,
                    ...updatedFields,
                    ...insuranceMeta,
                    date_sinistre: aiData.date_sinistre ? aiData.date_sinistre.split('T')[0] : prev.date_sinistre
                };
            });

            // 3. Clear the state in history so reloads or drawer changes don't trigger this again
            window.history.replaceState(null, '');
            toast.success('Données chargées depuis l\'IA');
        }
    }, [id, isEditing, location.state, assurances]);

    const fillVehicleData = useCallback((v) => {
        setForm(prev => ({
            ...prev,
            marque_vehicule: v.marque_vehicule || prev.marque_vehicule,
            modele_vehicule: v.modele_vehicule || prev.modele_vehicule,
            immatriculation: v.immatriculation || prev.immatriculation,
            annee_vehicule: v.annee_vehicule || prev.annee_vehicule,
            type_vehicule: v.type_vehicule || prev.type_vehicule,
            energie: v.energie || prev.energie,
            puissance: v.puissance || prev.puissance,
            carrosserie: v.carrosserie || prev.carrosserie,
            couleur: v.couleur || prev.couleur,
            numero_serie: v.numero_serie || prev.numero_serie,
            genre: v.genre || prev.genre,
            numero_police: v.numero_police || prev.numero_police,
            nom_assurance: v.nom_assurance || prev.nom_assurance,
            assurance_logo: v.assurance_logo || prev.assurance_logo,
            code_assurance: v.code_assurance || prev.code_assurance,
            ville_assurance: v.ville_assurance || prev.ville_assurance,
            etat_vehicule: v.etat_vehicule || prev.etat_vehicule,
        }));
        setShowVehiclesModal(false);
        toast.success('Données véhicule importées');
    }, []);

    // Client Autocomplete Logic
    useEffect(() => {
        if (!isEditing && form.nom_assure.length >= 3) {
            const timer = setTimeout(async () => {
                try {
                    setSearchingClients(true);
                    const res = await sinistresAPI.searchClients(form.nom_assure);
                    setClientSuggestions(res.data.data);
                } catch (e) {
                    console.error(e);
                } finally {
                    setSearchingClients(false);
                }
            }, 500);
            return () => clearTimeout(timer);
        } else {
            setClientSuggestions([]);
        }
    }, [form.nom_assure, isEditing]);

    const handleSelectClient = async (name) => {
        setForm(prev => ({ ...prev, nom_assure: name }));
        setClientSuggestions([]);

        try {
            const res = await sinistresAPI.getClientVehicles(name);
            const vehicles = res.data.data;
            if (vehicles.length === 0) return;

            if (vehicles.length === 1) {
                fillVehicleData(vehicles[0]);
            } else {
                setClientVehicles(vehicles);
                setShowVehiclesModal(true);
            }
        } catch (e) {
            console.error(e);
        }
    };

    // Brand/Model Auto-fill Logic
    useEffect(() => {
        const brand = marques.find(m => m.name && m.name.toUpperCase() === form.marque_vehicule?.toUpperCase());
        if (brand) {
            modelesAPI.list({ brand_id: brand.id, unique: true })
                .then(res => setModeles(res.data.data))
                .catch(() => setModeles([]));
        } else {
            setModeles([]);
            setModelYears([]);
        }
    }, [form.marque_vehicule, marques]);

    const handleModeleChange = (val) => {
        handleChange('modele_vehicule', val);
        if (!val) {
            setModelYears([]);
            return;
        }

        const brand = marques.find(m => m.name && m.name.toUpperCase() === form.marque_vehicule?.toUpperCase());
        if (brand) {
            // Fetch available years for this particular model
            modelesAPI.getYears(brand.id, val)
                .then(res => {
                    const years = res.data.data || [];
                    setModelYears(years);

                    // Auto-fill year if only one available or just to guide the user
                    if (years.length > 0 && !form.annee_vehicule) {
                        handleChange('annee_vehicule', years[0]);
                    }
                })
                .catch(() => setModelYears([]));
        }
    };


    const handleQuickAdd = async () => {
        if (!quickAdd.value.trim()) return;
        try {
            if (quickAdd.type === 'genre') {
                const res = await genresAPI.create({ genre: quickAdd.value });
                setGenres(prev => [...prev, { id: res.data.data.id, genre: quickAdd.value }]);
                handleChange('genre', quickAdd.value);
            } else if (quickAdd.type === 'carrosserie') {
                const res = await carosseriesAPI.create({ carosserie: quickAdd.value });
                setCarosseries(prev => [...prev, { id: res.data.data.id, carosserie: quickAdd.value }]);
                handleChange('carrosserie', quickAdd.value);
            } else if (quickAdd.type === 'color') {
                const res = await colorsAPI.create({ color: quickAdd.value });
                setColors(prev => [...prev, { id: res.data.data.id, color: quickAdd.value }]);
                handleChange('couleur', quickAdd.value);
            } else if (quickAdd.type === 'energie') {
                const res = await energiesAPI.create({ energie: quickAdd.value });
                setEnergies(prev => [...prev, { id: res.data.data.id, energie: quickAdd.value }]);
                handleChange('energie', quickAdd.value);
            } else if (quickAdd.type === 'estado') {
                const res = await estadosAPI.create({ estado: quickAdd.value });
                setEstados(prev => [...prev, { id: res.data.data.id, estado: quickAdd.value }]);
                handleChange('etat_vehicule', quickAdd.value);
            }
            setQuickAdd(null);
            playSound('quickadd');
            toast.success('Ajouté avec succès');
        } catch {
            toast.error('Erreur lors de l\'ajout');
        }
    };


    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handlePhotoUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        if (!isEditing) {
            toast.error("Veuillez d'abord enregistrer le sinistré en tant que brouillon pour ajouter des photos.");
            return;
        }

        const formData = new FormData();
        toast.loading('Compression des images en cours...', { id: 'upload' });

        try {
            const compressedFiles = await Promise.all(
                files.map(async (file) => {
                    const options = {
                        maxSizeMB: 1, // Compress to max 1MB
                        maxWidthOrHeight: 1600, // Reasonable max dimension for reports
                        useWebWorker: true,
                        initialQuality: 0.8
                    };
                    try {
                        return await imageCompression(file, options);
                    } catch (error) {
                        console.error('Compression failed for', file.name, error);
                        return file; // Fallback to original if compression fails
                    }
                })
            );

            compressedFiles.forEach((f, index) => {
                // Ensure filename is preserved or recreated since compression makes it a blob sometimes
                const finalFile = new File([f], files[index].name, { type: f.type });
                formData.append('photos', finalFile);
            });

            // Nettoyage de l'ID si c'est un format legacy (sz-xxx)
            const cleanSinistreId = id?.toString().replace(/^(sz-|s-)/, '');
            formData.append('sinistre_id', cleanSinistreId);
            if (form.numero_sinistre) formData.append('numero_sinistre', form.numero_sinistre);

            setUploadProgress(10);
            const res = await photosAPI.upload(formData, (e) => {
                if (e.total) setUploadProgress(Math.round(e.loaded / e.total * 100));
            });
            const newPhotos = res.data.data.map(p => {
                let photoUrl = p.url || p.file_path;
                if (!photoUrl || photoUrl.includes('\\') || (!photoUrl.startsWith('http') && !photoUrl.startsWith('/') && !photoUrl.startsWith('blob:'))) {
                    photoUrl = `${BASE_URL}/uploads/photos/${p.filename}`;
                } else if (photoUrl.startsWith('/')) {
                    // Local path like /uploads/photos/xxx — prepend backend URL
                    photoUrl = `${BASE_URL}${photoUrl}`;
                }
                return { ...p, url: photoUrl };
            });
            setPhotos(prev => [...prev, ...newPhotos]);
            playSound('upload');
            toast.success(`${files.length} photo(s) ajoutée(s)`, { id: 'upload' });
        } catch (err) {
            console.error('Upload error:', err);
            const msg = err.response?.data?.message || 'Erreur lors de l\'upload';
            toast.error(msg, { id: 'upload' });
        }
        finally { setUploadProgress(0); }
    };

    const removePhoto = async (photoId, idx) => {
        try {
            await photosAPI.delete(photoId);
            setPhotos(prev => prev.filter((_, i) => i !== idx));
            toast.success('Photo supprimée');
        } catch { toast.error('Erreur lors de la suppression'); }
    };

    const handleAssuranceSelect = (e) => {
        const name = e.target.value;
        const matching = assurances.filter(a => a.name === name);
        if (matching.length === 1) {
            setForm(prev => ({
                ...prev,
                nom_assurance: name,
                assurance_logo: matching[0].logo || '',
                code_assurance: matching[0].code || '',
                ville_assurance: matching[0].city || '',
            }));
        } else {
            setForm(prev => ({
                ...prev,
                nom_assurance: name,
                assurance_logo: '',
                code_assurance: '',
                ville_assurance: '',
            }));
        }
    };

    const handleSubmit = async (statut) => {
        let newErrors = {};
        // numero_sinistre is now OPTIONAL (company ref), only nom_assure is required
        if (!form.nom_assure?.trim()) newErrors.nom_assure = true;

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error('Veuillez remplir les champs obligatoires');
            return;
        }

        setErrors({});
        setSaving(true);
        try {
            const data = { ...form, statut };
            if (isEditing) {
                await sinistresAPI.update(id, data);
                playSound('save');
                toast.success('Sinistré mis à jour avec succès');
            } else {
                const res = await sinistresAPI.create(data);
                const ref = res.data.data.reference_interne || res.data.data.numero_sinistre;
                playSound('save');
                toast.success('Sinistré créé — Réf. Interne: ' + ref);
                // Redirect to edit mode so user can add photos immediately
                navigate(`/sinistres/edit/${res.data.data.id}`);
                return;
            }
            navigate('/sinistres');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Layout title="Chargement..."><div className="loading-spinner"><div className="spinner" /></div></Layout>;

    return (
        <Layout title={isEditing ? 'Modifier le Sinistré' : 'Nouveau Sinistré'}>
            <div style={{ maxWidth: '1000px' }}>
                {/* Header */}
                <div className="flex-between mb-4">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
                            <ArrowLeft size={16} />
                        </button>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <h2 style={{ fontSize: '20px', fontWeight: '800', color: 'var(--primary-800)', margin: 0 }}>
                                    {isEditing ? `Modifier le Sinistré` : 'Nouveau Sinistré'}
                                </h2>
                                {isEditing && form.assurance_logo && (
                                    <img
                                        src={`${BASE_URL}/assurances_logo/${form.assurance_logo}`}
                                        alt="Logo Assurance"
                                        style={{ height: '80px', maxWidth: '200px', objectFit: 'contain', marginLeft: '15px' }}
                                    />
                                )}
                            </div>
                            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                {isEditing
                                    ? form.reference_interne ? `Réf. Interne: ${form.reference_interne}` : 'Modification en cours'
                                    : 'Remplissez les informations ci-dessous'}
                            </p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-outline" onClick={() => handleSubmit('brouillon')} disabled={saving}>
                            <Save size={15} /> Enregistrer brouillon
                        </button>
                        <button className="btn btn-primary" onClick={() => handleSubmit('en_cours')} disabled={saving}>
                            {saving ? <><div className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} /> Sauvegarde...</>
                                : <><Save size={15} /> Finaliser</>}
                        </button>
                    </div>
                </div>

                {/* Section 1: Identification & Assuré */}
                <div className="card" style={{ marginBottom: '16px' }}>
                    <div className="form-section-title"><User size={14} style={{ display: 'inline', marginRight: '6px' }} />Identification &amp; Assuré</div>
                    {/* Show internal reference badge when editing */}
                    {isEditing && form.reference_interne && (
                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' }}>Notre Réf. Interne</span>
                            <span style={{
                                fontFamily: 'monospace', fontWeight: '900', fontSize: '15px',
                                color: 'var(--primary-700)', background: 'var(--primary-50)',
                                padding: '4px 12px', borderRadius: '8px', border: '1.5px solid var(--primary-200)',
                                letterSpacing: '1px'
                            }}>{form.reference_interne}</span>
                            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>(auto-généré, non modifiable)</span>
                        </div>
                    )}
                    <div className="form-grid form-grid-3">
                        <div className="form-group">
                            <label className="form-label">
                                N° Sin.Assur
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="N° Sinistre fourni par l'assurance"
                                value={form.numero_sinistre || ''}
                                onChange={(e) => {
                                    handleChange('numero_sinistre', e.target.value);
                                }}
                                style={{
                                    fontWeight: '700',
                                    color: 'var(--primary-700)',
                                    fontFamily: 'monospace',
                                }}
                            />
                        </div>
                        <div className="form-group" style={{ position: 'relative' }}>
                            <label className="form-label" style={{ color: errors.nom_assure ? 'var(--danger)' : '' }}>
                                Nom & Prénom Assuré *
                            </label>
                            <input
                                type="text"
                                className={`form-control ${errors.nom_assure ? 'error' : ''}`}
                                placeholder="Nom complet de l'assuré"
                                value={form.nom_assure}
                                onChange={(e) => {
                                    handleChange('nom_assure', e.target.value);
                                    if (errors.nom_assure) setErrors(prev => ({ ...prev, nom_assure: false }));
                                }}
                                autoComplete="off"
                                style={{
                                    border: errors.nom_assure ? '1.5px solid var(--danger)' : '',
                                    background: errors.nom_assure ? '#fff1f1' : ''
                                }}
                            />
                            {searchingClients && <div style={{ position: 'absolute', right: '10px', top: '35px' }} className="spinner" />}
                            {Array.isArray(clientSuggestions) && clientSuggestions.length > 0 && (
                                <div className="card" style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                                    padding: '5px', marginTop: '5px', boxShadow: 'var(--shadow-lg)'
                                }}>
                                    {clientSuggestions.map((s, i) => (
                                        <div key={i} className="table-row-hover" style={{
                                            padding: '8px 12px', cursor: 'pointer', borderRadius: '4px',
                                            fontSize: '13px', fontWeight: '600', display: 'flex', justifyContent: 'space-between'
                                        }} onClick={() => handleSelectClient(s.name)}>
                                            <span>{s.name || '—'}</span>
                                            {s.plate && <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'normal' }}>{s.plate}</span>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <Field label="N° Police d'Assurance" field="numero_police" form={form} handleChange={handleChange} placeholder="N° police" />
                        <Field label="Date de l'accident" field="date_sinistre" form={form} handleChange={handleChange} type="date" />
                        <Field label="État général du véhicule">
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <select className="form-control" value={form.etat_vehicule || ''} onChange={e => handleChange('etat_vehicule', e.target.value)}>
                                    <option value="">Sélectionner...</option>
                                    {Array.isArray(estados) && estados.map(st => <option key={st.id} value={st.estado}>{st.estado}</option>)}
                                </select>
                                <button type="button" className="btn btn-ghost" onClick={() => setQuickAdd({ type: 'estado', value: '' })} style={{ padding: '0 10px' }}><Plus size={16} /></button>
                            </div>
                        </Field>
                    </div>
                </div>

                {/* Modal de sélection de véhicule */}
                {showVehiclesModal && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', padding: '20px'
                    }}>
                        <div className="card" style={{ width: '100%', maxWidth: '600px', padding: '24px' }}>
                            <div className="flex-between mb-4">
                                <h3 style={{ fontSize: '18px', fontWeight: '800' }}>Plusieurs véhicules trouvés pour cet assuré</h3>
                                <button className="btn btn-ghost btn-sm" onClick={() => setShowVehiclesModal(false)}><X size={18} /></button>
                            </div>
                            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '16px' }}>
                                Veuillez sélectionner le véhicule concerné par ce nouveau dossier :
                            </p>
                            <div style={{ display: 'grid', gap: '10px' }}>
                                {Array.isArray(clientVehicles) && clientVehicles.map((v, i) => (
                                    <div key={i} className="card table-row-hover" style={{
                                        padding: '16px', cursor: 'pointer', border: '1px solid var(--border)',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                    }} onClick={() => fillVehicleData(v)}>
                                        <div>
                                            <div style={{ fontWeight: '700', fontSize: '15px' }}>{v.marque_vehicule} {v.modele_vehicule}</div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                                {v.genre} • {v.annee_vehicule} • {v.energie} • {v.etat_vehicule}
                                            </div>
                                        </div>
                                        <Plate value={v.immatriculation} />
                                    </div>
                                ))}
                            </div>
                            <button className="btn btn-outline w-100 mt-4" onClick={() => setShowVehiclesModal(false)}>
                                Saisir manuellement (Nouveau véhicule)
                            </button>
                        </div>
                    </div>
                )}

                <div className="card" style={{ marginBottom: '16px' }}>
                    <div className="form-section-title"><Car size={14} style={{ display: 'inline', marginRight: '6px' }} />Caractéristiques du Véhicule & Mission</div>
                    <div className="form-grid form-grid-3">
                        <Field label="Marque">
                            <input list="marquesList" className="form-control" placeholder="Marque du véhicule"
                                value={form.marque_vehicule || ''} onChange={e => handleChange('marque_vehicule', e.target.value)} />
                            <datalist id="marquesList">{Array.isArray(marques) && marques.map(m => <option key={m.id} value={m.name} />)}</datalist>
                        </Field>
                        <Field label="Modèle">
                            <input list="modelesList" className="form-control" placeholder="Modèle"
                                value={form.modele_vehicule || ''} onChange={e => handleModeleChange(e.target.value)} />
                            <datalist id="modelesList">{Array.isArray(modeles) && modeles.map(m => <option key={m.id} value={m.name} />)}</datalist>
                        </Field>
                        <Field label="Immatriculation" field="immatriculation" form={form} handleChange={handleChange} placeholder="Ex: 123456 - 16" />
                        <Field label="Année">
                            <input list="yearsList" className="form-control" type="number" placeholder="2020"
                                value={form.annee_vehicule || ''} onChange={e => handleChange('annee_vehicule', e.target.value)} />
                            <datalist id="yearsList">{Array.isArray(modelYears) && modelYears.map(y => <option key={y} value={y} />)}</datalist>
                        </Field>
                        <Field label="Type" field="type_vehicule" form={form} handleChange={handleChange} placeholder="Type de véhicule" />
                        <Field label="Genre">
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <select className="form-control" value={form.genre || ''} onChange={e => handleChange('genre', e.target.value)}>
                                    <option value="">Sélectionner...</option>
                                    {Array.isArray(genres) && genres.map(g => <option key={g.id} value={g.genre}>{g.genre}</option>)}
                                </select>
                                <button type="button" className="btn btn-ghost" onClick={() => setQuickAdd({ type: 'genre', value: '' })} style={{ padding: '0 10px' }}><Plus size={16} /></button>
                            </div>
                        </Field>
                        <Field label="Énergie">
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <select className="form-control" value={form.energie || ''} onChange={e => handleChange('energie', e.target.value)}>
                                    <option value="">Sélectionner...</option>
                                    {Array.isArray(energies) && energies.map(en => <option key={en.id} value={en.energie}>{en.energie}</option>)}
                                </select>
                                <button type="button" className="btn btn-ghost" onClick={() => setQuickAdd({ type: 'energie', value: '' })} style={{ padding: '0 10px' }}><Plus size={16} /></button>
                            </div>
                        </Field>
                        <Field label="Puissance (CV)" field="puissance" form={form} handleChange={handleChange} type="number" placeholder="Puissance" />
                        <Field label="Carrosserie">
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <select className="form-control" value={form.carrosserie || ''} onChange={e => handleChange('carrosserie', e.target.value)}>
                                    <option value="">Sélectionner...</option>
                                    {Array.isArray(carosseries) && carosseries.map(c => <option key={c.id} value={c.carosserie}>{c.carosserie}</option>)}
                                </select>
                                <button type="button" className="btn btn-ghost" onClick={() => setQuickAdd({ type: 'carrosserie', value: '' })} style={{ padding: '0 10px' }}><Plus size={16} /></button>
                            </div>
                        </Field>
                        <Field label="Couleur">
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <select className="form-control" value={form.couleur || ''} onChange={e => handleChange('couleur', e.target.value)}>
                                    <option value="">Sélectionner...</option>
                                    {Array.isArray(colors) && colors.map(c => <option key={c.id} value={c.color}>{c.color}</option>)}
                                </select>
                                <button type="button" className="btn btn-ghost" onClick={() => setQuickAdd({ type: 'color', value: '' })} style={{ padding: '0 10px' }}><Plus size={16} /></button>
                            </div>
                        </Field>
                        <Field label="N° de Série" field="numero_serie" form={form} handleChange={handleChange} placeholder="Numéro de châssis" />
                    </div>
                </div>

                {/* Section 3: Tiers */}
                <div className="card" style={{ marginBottom: '16px' }}>
                    <div className="form-section-title"><Users size={14} style={{ display: 'inline', marginRight: '6px' }} />Informations du Tiers</div>
                    <div className="form-grid">
                        <Field label="Nom du Tiers" field="nom_tiers" form={form} handleChange={handleChange} placeholder="Nom du tiers" />
                    </div>
                </div>

                {/* Section 4: Assurance */}
                <div className="card" style={{ marginBottom: '16px' }}>
                    <div className="form-section-title"><Shield size={14} style={{ display: 'inline', marginRight: '6px' }} />Compagnie d'Assurance</div>

                    {/* Unique Company List */}
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'start', marginBottom: '12px' }}>
                        <div style={{ flex: 1 }}>
                            <Field label="Nom de l'Assurance">
                                <select className="form-control" value={form.nom_assurance || ''} onChange={handleAssuranceSelect}>
                                    <option value="">Sélectionner une compagnie...</option>
                                    {Array.isArray(assurances) && [...new Set(assurances.filter(a => a && a.name).map(a => a.name))].sort().map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>
                        {form.assurance_logo && (
                            <img
                                src={`${BASE_URL}/assurances_logo/${form.assurance_logo}`}
                                alt="Logo Assurance"
                                style={{ height: '80px', maxWidth: '150px', objectFit: 'contain', marginLeft: '20px' }}
                            />
                        )}
                    </div>

                    <div className="form-grid form-grid-3">
                        {/* Branch Selector if multiple exist for the chosen name */}
                        {Array.isArray(assurances) && assurances.filter(a => a && a.name === form.nom_assurance).length > 1 ? (
                            <Field label="Choisir l'Agence / Succursale">
                                <select
                                    className="form-control"
                                    style={{ border: '2px solid var(--primary-200)', background: 'var(--primary-50)' }}
                                    onChange={(e) => {
                                        const ass = assurances.find(a => a.id === parseInt(e.target.value));
                                        if (ass) {
                                            setForm(prev => ({ ...prev, code_assurance: ass.code, ville_assurance: ass.city, assurance_logo: ass.logo || '' }));
                                        }
                                    }}
                                >
                                    <option value="">-- Sélectionner l'agence --</option>
                                    {assurances.filter(a => a && a.name === form.nom_assurance).map(a => (
                                        <option key={a.id} value={a.id}>{a.city} (Code: {a.code})</option>
                                    ))}
                                </select>
                            </Field>
                        ) : null}

                        <Field label="Code Assurance" field="code_assurance" form={form} handleChange={handleChange} placeholder="Code" />
                        <Field label="Ville / Agence" field="ville_assurance" form={form} handleChange={handleChange} placeholder="Ville" />
                    </div>
                </div>

                {/* Section 5: Photos */}
                <div className="card" style={{ marginBottom: '16px' }}>
                    <div className="form-section-title" style={{ margin: 0, marginBottom: '16px' }}>📷 Photos de Constatation ({photos.length})</div>

                    {!isEditing ? (
                        <div style={{ background: 'var(--primary-50)', padding: '16px', borderRadius: 'var(--radius)', color: 'var(--primary-700)', fontSize: '14px', textAlign: 'center' }}>
                            💡 Créez d'abord le dossier (Brouillon) pour afficher l'appareil photo et capturer les photos rapidement.
                        </div>
                    ) : (
                        <div style={{ marginBottom: '24px' }}>
                            <label className="btn btn-gold w-full" style={{ padding: '20px', fontSize: '18px', cursor: 'pointer', marginBottom: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                                <Camera size={24} strokeWidth={3} /> PRENDRE UNE PHOTO
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    style={{ display: 'none' }}
                                    onChange={handlePhotoUpload}
                                />
                            </label>

                            <label className="btn btn-outline w-full" style={{ padding: '16px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                                <Upload size={20} /> Choisir depuis la galerie
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={handlePhotoUpload}
                                />
                            </label>
                        </div>
                    )}

                    {uploadProgress > 0 && (
                        <div style={{ background: 'var(--surface-2)', borderRadius: '10px', height: '10px', overflow: 'hidden', marginBottom: '20px' }}>
                            <div style={{ height: '100%', background: 'var(--primary-500)', width: `${uploadProgress}%`, transition: 'width 0.3s' }} />
                        </div>
                    )}

                    {Array.isArray(photos) && photos.length > 0 && (
                        <div className="photos-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
                            {photos.map((photo, idx) => (
                                <div key={idx} className="photo-item" style={{ aspectRatio: '1', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
                                    <img src={photo.url} alt={photo.original_name || `${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: 'white', fontSize: '10px', padding: '4px', textAlign: 'center' }}>
                                        Photo {idx + 1}
                                    </div>
                                    <button className="photo-delete" onClick={() => removePhoto(photo.id, idx)} title="Supprimer" style={{ position: 'absolute', top: '4px', right: '4px', background: 'var(--danger)', color: 'white', border: 'none', borderRadius: '50%', padding: '4px', cursor: 'pointer' }}>
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Section 6: Lieu */}
                <div className="card">
                    <div className="form-section-title">📍 Lieu</div>
                    <div className="form-grid">
                        <Field label="Lieu de Visite" field="lieu_visite" form={form} handleChange={handleChange} placeholder="Lieu où le véhicule est visité" dictation />
                    </div>
                </div>

                {/* Bottom Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                    <button className="btn btn-ghost" onClick={() => navigate(-1)}>Annuler</button>
                    <button className="btn btn-outline" onClick={() => handleSubmit('brouillon')} disabled={saving}>
                        <Save size={15} /> Brouillon
                    </button>
                    <button className="btn btn-primary" onClick={() => handleSubmit('en_cours')} disabled={saving}>
                        {saving ? 'Sauvegarde...' : (<><Save size={15} /> Finaliser</>)}
                    </button>
                </div>
            </div>
            {quickAdd && (
                <div className="modal-overlay">
                    <div className="modal" style={{ maxWidth: '400px' }}>
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {quickAdd.type === 'genre' && 'Ajouter un nouveau Genre'}
                                {quickAdd.type === 'carrosserie' && 'Ajouter une nouvelle Carrosserie'}
                                {quickAdd.type === 'color' && 'Ajouter une nouvelle Couleur'}
                                {quickAdd.type === 'energie' && 'Ajouter une nouvelle Énergie'}
                                {quickAdd.type === 'estado' && 'Ajouter un nouvel État'}
                            </h3>
                            <button className="modal-close" onClick={() => setQuickAdd(null)}><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Valeur"
                                value={quickAdd.value}
                                onChange={e => setQuickAdd({ ...quickAdd, value: e.target.value })}
                                autoFocus
                            />
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-ghost" onClick={() => setQuickAdd(null)}>Annuler</button>
                            <button className="btn btn-primary" onClick={handleQuickAdd}>Ajouter</button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default SinistreForm;
